import { useState } from "react";
import { useLatest } from "../../../hooks";
import {
  fetchSuggestions,
  generateAppointmentDates,
  timeStringToHour,
} from "../utils";

/**
 *
 * Custom hook to fetch and manage reservation suggestions
 * @param {Array} selectedClients - Array of selected clients with their options.
 * @param {number} index - Index of the selected client in the array.
 * @param {Object} reservationSettingState - Reservation settings including calendar range, weekdays, time parts, and custom time.
 * @returns {Object} - Contains suggestions and loading state.
 */
export const useSuggestions = (
  selectedClients,
  index,
  reservationSettingState,
) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const { selectedLocation } = selectedClients[0]; // All clients share the same location

  const { selectedClient, selectedService, selectedDoctors } =
    selectedClients[index];

  const {
    isJointConsultation,
    calendarRange,
    selectedWeekdays,
    selectedTimeParts,
    customTimeFrom,
    customTimeTo,
    order_from_h,
  } = reservationSettingState;

  const groupSize = selectedClients.length;
  const isSelectedClient = selectedClient != null;
  const with_selected_doctor_only = selectedClient?.with_selected_doctor_only;
  const selectedLocationId = selectedLocation?.id;

  useLatest(
    async (signal) => {
      if (
        !isSelectedClient ||
        selectedLocationId == null ||
        selectedService == null
      ) {
        setSuggestions([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const suggestions = await fetchSuggestionsWithRetry(signal, {
          locationId: selectedLocationId,
          serviceId: selectedService.id,
          duration:
            selectedService.duration ??
            selectedService.locationService.duration,
          selectedDoctors,
          groupSize,
          isJointConsultation,
          calendarRange,
          selectedWeekdays,
          selectedTimeParts,
          customTimeFrom,
          customTimeTo,
          order_from_h,
          with_selected_doctor_only: with_selected_doctor_only,
        });

        if (signal.aborted) return;

        setSuggestions(suggestions);
        setLoading(false);
      } catch (_) {
        if (!signal.aborted) {
          setSuggestions([]);
          setLoading(false);
        }
      }
    },
    [
      isSelectedClient,
      with_selected_doctor_only,
      selectedLocationId,
      selectedService,
      selectedDoctors,
      groupSize,
      isJointConsultation,
      calendarRange,
      selectedWeekdays,
      selectedTimeParts,
      customTimeFrom,
      customTimeTo,
    ],
  );

  return { suggestions, loading };
};

function selectedDoctorsFilter(selectedDoctors, with_selected_doctor_only) {
  if (!with_selected_doctor_only) {
    return {};
  }

  if (selectedDoctors?.any) {
    return { employee_clinis_position: "Doctor" };
  }

  if (selectedDoctors?.doctors?.length !== 0) {
    return { employee_ids: selectedDoctors.doctors.map((d) => d.id) };
  }

  return {};
}

/**
 * Fetches suggestions with automatic retry logic
 * @param {AbortSignal} signal
 * @param {Object} params
 * @returns {Promise<Array>}
 */
const fetchSuggestionsWithRetry = async (signal, params) => {
  const {
    locationId,
    serviceId,
    duration,
    selectedDoctors,
    groupSize,
    isJointConsultation,
    calendarRange,
    selectedWeekdays,
    selectedTimeParts,
    customTimeFrom,
    customTimeTo,
    order_from_h,
    with_selected_doctor_only,
  } = params;

  const MAX_ROUNDS = 10; // Prevent infinite loops
  const MIN_SUGGESTIONS = 5;

  let generationRound = 0;
  let allSuggestions = [];

  while (
    allSuggestions.length < MIN_SUGGESTIONS &&
    generationRound < MAX_ROUNDS
  ) {
    // Check for cancellation before each iteration
    if (signal.aborted) {
      throw new Error("Request cancelled");
    }

    generationRound++;

    try {
      const days = generateAppointmentDates(
        generationRound,
        calendarRange,
        selectedWeekdays,
        selectedTimeParts,
        timeStringToHour(customTimeFrom),
        timeStringToHour(customTimeTo),
        order_from_h,
      );

      const filter = {
        location_id: locationId,
        service_id: serviceId,
        duration,
        days,
        group_size: isJointConsultation ? groupSize : 1,
        ...selectedDoctorsFilter(selectedDoctors, with_selected_doctor_only),
      };

      const suggestions = await fetchSuggestions(signal, filter);

      // Merge suggestions and remove duplicates if needed
      allSuggestions = mergeSuggestions(
        allSuggestions,
        suggestions,
        generationRound,
      );
    } catch (error) {
      // If it's an abort error, re-throw it
      if (error.name === "AbortError" || signal.aborted) {
        throw error;
      }

      // Log other errors but continue trying
      console.warn(
        `Failed to fetch suggestions for round ${generationRound}:`,
        error,
      );

      // If we're on the last round and still have no suggestions, throw
      if (generationRound === MAX_ROUNDS && allSuggestions.length === 0) {
        throw new Error(
          "Failed to fetch any suggestions after multiple attempts",
        );
      }
    }
  }

  return allSuggestions;
};

/**
 * Merges new suggestions with existing ones, removing duplicates
 * @param {Array} existing
 * @param {Array} newSuggestions
 * @returns {Array}
 */
const mergeSuggestions = (existing, newSuggestions, generationRound) => {
  const existingIds = new Set(existing.map((s) => s.id));
  const uniqueNew = newSuggestions
    .filter((s) => !existingIds.has(s.id))
    .map((s) => {
      return { ...s, generationRound };
    });
  return [...existing, ...uniqueNew];
};
